
import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Switch,
  FormControlLabel,
  RadioGroup,
  Radio,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormLabel,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close'; // Import CloseIcon
import { IconButton } from '@mui/material';
import api from '../../services/api';

const CustomerUsers = ({ customerId, handleClose }) => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [openVirtualNumberModal, setOpenVirtualNumberModal] = useState(false);
  const [selectedUserForCt, setSelectedUserForCt] = useState(null);
  const [availableVirtualNumbers, setAvailableVirtualNumbers] = useState([]);
  const [selectedVirtualNumber, setSelectedVirtualNumber] = useState(null);

  useEffect(() => {
    const fetchUsers = async () => {
      try {
        setLoading(true);
        setError(null);
        const response = await api.get(`/account/${customerId}/users/`);
        if (!response.data.success) {
          throw new Error(response.data.message || "Failed to fetch users");
        }
        setUsers(response.data.data);
      } catch (error) {
        console.error("Error fetching users:", error);
        setError(`Failed to load user data: ${error.message || "Unknown error"}`);
      } finally {
        setLoading(false);
      }
    };

    if (customerId) {
      fetchUsers();
    }
  }, [customerId]);

  const fetchVirtualNumbers = async (accountId) => {
    try {
      const response = await api.get(`/account/${accountId}/virtual-numbers/`);
      if (!response.data.success) {
        throw new Error(response.data.message || "Failed to fetch virtual numbers");
      }
      setAvailableVirtualNumbers(response.data.data);
      if (response.data.data.length === 1) {
        setSelectedVirtualNumber(response.data.data[0].id);
      } else {
        setSelectedVirtualNumber(null);
      }
    } catch (error) {
      console.error("Error fetching virtual numbers:", error);
      setError(`Failed to load virtual numbers: ${error.message || "Unknown error"}`);
    }
  };

  const handleCtToggleChange = async (user) => {
    setSelectedUserForCt(user);
    if (user.is_cloud_telephony_enabled) {
      // If CT is currently enabled, disable it directly
      try {
        await api.post(`/account/${customerId}/user/${user.id}/disable-ct/`);
        setUsers((prevUsers) =>
          prevUsers.map((u) =>
            u.id === user.id ? { ...u, is_cloud_telephony_enabled: false } : u
          )
        );
      } catch (error) {
        console.error("Error disabling CT:", error);
        const errorMessage = error.response?.data?.message || error.message || "Unknown error";
        setError(`Failed to disable CT for ${user.name}: ${errorMessage}`);
      }
    } else {
      // If CT is currently disabled, open modal to enable
      await fetchVirtualNumbers(customerId);
      setOpenVirtualNumberModal(true);
    }
  };

  const handleVirtualNumberSelect = (event) => {
    setSelectedVirtualNumber(event.target.value);
  };

  const handleConfirmCtEnable = async () => {
    if (!selectedUserForCt || !selectedVirtualNumber) return;

    try {
      await api.post(`/account/${customerId}/user/${selectedUserForCt.id}/enable-ct/`, {
        virtual_number_id: selectedVirtualNumber,
      });
      setUsers((prevUsers) =>
        prevUsers.map((u) =>
          u.id === selectedUserForCt.id
            ? { ...u, is_cloud_telephony_enabled: true, virtual_number_detail: availableVirtualNumbers.find(vn => vn.id === selectedVirtualNumber) }
            : u
        )
      );
      setOpenVirtualNumberModal(false);
      setSelectedUserForCt(null);
      setSelectedVirtualNumber(null);
    } catch (error) {
      console.error("Error enabling CT:", error);
      const errorMessage = error.response?.data?.message || error.message || "Unknown error";
      setError(`Failed to enable CT for ${selectedUserForCt.name}: ${errorMessage}`);
    }
  };

  const handleCloseVirtualNumberModal = () => {
    setOpenVirtualNumberModal(false);
    setSelectedUserForCt(null);
    setSelectedVirtualNumber(null);
  };

  if (loading) {
    return <Box sx={{ p: 3 }}><Typography>Loading users...</Typography></Box>;
  }

  if (error) {
    return <Box sx={{ p: 3 }}><Typography color="error">Error: {error}</Typography></Box>;
  }

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>
        Users for Account ID: {customerId}
        <IconButton
          aria-label="close"
          onClick={handleClose}
          sx={{
            position: 'absolute',
            right: 8,
            top: 8,
            color: (theme) => theme.palette.grey[500],
          }}
        >
          <CloseIcon />
        </IconButton>
      </Typography>

      <TableContainer component={Paper}>
        <Table sx={{ minWidth: 650 }} aria-label="customer users table">
          <TableHead>
            <TableRow>
              <TableCell>Name & Designation</TableCell>
              <TableCell>Mobile</TableCell>
              <TableCell align="center">CT Enabled</TableCell>
              <TableCell>CT Agent ID</TableCell>
              <TableCell>Virtual Number</TableCell>
              <TableCell>Role</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {users.map((user) => (
              <TableRow
                key={user.id}
                sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
              >
                <TableCell component="th" scope="row">
                  {user.name}
                  <Typography variant="body2" color="text.secondary">
                    {user.designation || 'N/A'}
                  </Typography>
                </TableCell>
                <TableCell>{user.mobile}</TableCell>
                <TableCell align="center">
                  <FormControlLabel
                    control={
                      <Switch
                        checked={user.is_cloud_telephony_enabled}
                        onChange={() => handleCtToggleChange(user)}
                        name="isCloudTelephonyEnabled"
                        color="primary"
                      />
                    }
                    label={user.is_cloud_telephony_enabled ? 'Enabled' : 'Disabled'}
                    labelPlacement="top"
                  />
                </TableCell>
                <TableCell>{user.cloud_telephony_agent_id || 'N/A'}</TableCell>
                <TableCell>{user.virtual_number_detail?.number || 'N/A'}</TableCell>
                <TableCell>{user.role_display || 'N/A'}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Virtual Number Selection Modal */}
      <Dialog open={openVirtualNumberModal} onClose={handleCloseVirtualNumberModal}>
        <DialogTitle>Select Virtual Number</DialogTitle>
        <DialogContent>
          <FormLabel component="legend">Available Virtual Numbers</FormLabel>
          <RadioGroup
            aria-label="virtual-number"
            name="virtual-number-group"
            value={selectedVirtualNumber}
            onChange={handleVirtualNumberSelect}
          >
            {availableVirtualNumbers.length > 0 ? (
              availableVirtualNumbers.map((vn) => (
                <FormControlLabel
                  key={vn.id}
                  value={vn.id}
                  control={<Radio />}
                  label={vn.number}
                />
              ))
            ) : (
              <Typography>No virtual numbers available.</Typography>
            )}
          </RadioGroup>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseVirtualNumberModal}>Cancel</Button>
          <Button
            onClick={handleConfirmCtEnable}
            color="primary"
            disabled={!selectedVirtualNumber}
          >
            Confirm
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CustomerUsers;
